import { type CSSProperties, type ReactNode, useId } from 'react';
import { DEEP, GRAD, type Grad, INK, INK_FAINT, grad } from '../design/forms';
import type { FilledIcon } from '../design/icons';

/*
 * The pictures of «Моє шкільне життя» in «Форми 2» (28.10): the few things the set of LineArt has no
 * picture for — a rocket (the class season, a buddy), a crown, a rainbow, a face in sunglasses (the
 * buddies of the shop), an envelope (a letter to the future), a photo (the album). Drawn as LineArt is:
 * one loose ink line over one soft shape in the deep gradient of the card's colour; a `ghost` is the big
 * quiet picture behind a card's words. And the two small parts the pages of the group share: a label
 * (a soft rectangle, never a pill) and an icon in a gradient circle.
 */

export type LifeArtKind = 'rocket' | 'crown' | 'rainbow' | 'shades' | 'letter' | 'photo';

const line = {
  fill: 'none',
  stroke: INK,
  strokeWidth: 2.6,
  strokeLinecap: 'round' as const,
  strokeLinejoin: 'round' as const,
};

interface Paint {
  deep: string;
  soft: string;
  dot: string;
}

const ART: Record<LifeArtKind, (p: Paint) => ReactNode> = {
  // a rocket on its way up, a short trail behind
  rocket: (p) => (
    <>
      <circle cx="40" cy="22" r="15" fill={p.deep} />
      <g transform="rotate(40 32 30)">
        <path d="M24 24C24 15 28 9 32 5C36 9 40 15 40 24V42H24Z" {...line} fill="#FFFFFF" />
        <circle cx="32" cy="24" r="4" {...line} strokeWidth={2.2} />
        <path d="M24 32L16 42V47L24 43M40 32L48 42V47L40 43" {...line} />
        <path d="M28 46C28 51 32 55 32 55C32 55 36 51 36 46" {...line} fill={p.soft} />
      </g>
      <path d="M4 60C8 55 12 58 15 53" {...line} strokeWidth={2.2} />
    </>
  ),
  // a crown, its three tips with a dot each
  crown: (p) => (
    <>
      <ellipse cx="34" cy="38" rx="22" ry="14" fill={p.deep} />
      <path d="M12 46L9 20L22 31L32 13L42 31L55 20L52 46Z" {...line} />
      <path d="M14 54H50" {...line} />
      <circle cx="9" cy="20" r="3" fill={INK} />
      <circle cx="32" cy="13" r="3" fill={INK} />
      <circle cx="55" cy="20" r="3" fill={INK} />
    </>
  ),
  // a rainbow over two clouds
  rainbow: (p) => (
    <>
      <path
        d="M11 46C11 31 20 21 32 21C44 21 53 31 53 46"
        fill="none"
        stroke={p.deep}
        strokeWidth="10"
        strokeLinecap="round"
      />
      <path d="M4 44C4 25 16 13 32 13C48 13 60 25 60 44" {...line} />
      <path d="M19 44C19 34 25 28 32 28C39 28 45 34 45 44" {...line} />
      <path
        d="M3 54C3 49 8 47 11 50C13 46 20 47 20 52C23 52 24 56 21 57H5C3 57 3 55 3 54Z"
        {...line}
        fill="#FFFFFF"
      />
      <path
        d="M44 54C44 49 49 47 52 50C54 46 61 47 61 52C63 53 63 57 60 57H46C44 57 44 55 44 54Z"
        {...line}
        fill="#FFFFFF"
      />
    </>
  ),
  // a face in sunglasses, smiling
  shades: (p) => (
    <>
      <circle cx="32" cy="33" r="22" fill={p.deep} />
      <path d="M11 26H53" {...line} />
      <path d="M15 26C15 36 27 36 28 26Z" fill={INK} />
      <path d="M36 26C37 36 49 36 49 26Z" fill={INK} />
      <path d="M23 44C28 48 36 48 41 44" {...line} />
      <path d="M50 10L54 6M56 16L61 14" {...line} strokeWidth={2.2} />
    </>
  ),
  // a sealed envelope, a loop of its way
  letter: (p) => (
    <>
      <rect x="14" y="20" width="44" height="32" rx="5" fill={p.deep} />
      <rect x="6" y="13" width="44" height="32" rx="5" {...line} fill="#FFFFFF" />
      <path d="M7 16L28 32L49 16" {...line} />
      <circle cx="28" cy="32" r="4.5" fill={p.dot} />
      <path d="M44 56C50 60 58 58 61 50" {...line} strokeWidth={2.2} />
    </>
  ),
  // a photo: a hill and the sun on it
  photo: (p) => (
    <>
      <rect x="15" y="17" width="44" height="36" rx="6" fill={p.deep} />
      <rect x="6" y="10" width="44" height="36" rx="6" {...line} fill="#FFFFFF" />
      <path d="M10 40L21 28L31 37L37 32L46 40" {...line} />
      <circle cx="37" cy="20" r="4" fill={p.dot} />
    </>
  ),
};

/** A picture of the group: a 64×64 box in the gradients of its colour; `ghost` — big and quiet behind. */
export function LifeArt({
  kind,
  tone,
  size = 52,
  ghost = false,
  className = '',
}: {
  kind: LifeArtKind;
  /** the colour of the card it stands on */
  tone: Grad;
  size?: number;
  ghost?: boolean;
  className?: string;
}) {
  const id = `life${useId().replace(/:/g, '')}`;
  const [a, b] = DEEP[tone];
  return (
    <svg
      viewBox="0 0 64 64"
      width={size}
      height={size}
      aria-hidden
      focusable="false"
      className={`shrink-0 overflow-visible ${ghost ? 'art-ghost' : ''} ${className}`}
      style={ghost ? ({ '--sh-ink': `url(#${id}l)` } as CSSProperties) : undefined}
    >
      <defs>
        <linearGradient id={`${id}d`} x1="0.1" y1="0" x2="0.9" y2="1">
          <stop offset="0" stopColor={ghost ? GRAD[tone].to : a} />
          <stop offset="1" stopColor={ghost ? a : b} />
        </linearGradient>
        <linearGradient id={`${id}s`} x1="0.1" y1="0" x2="0.9" y2="1">
          <stop offset="0" stopColor={GRAD[tone].to} stopOpacity={ghost ? 0.6 : 1} />
          <stop offset="1" stopColor={a} stopOpacity={ghost ? 0.6 : 1} />
        </linearGradient>
        {ghost && (
          <linearGradient id={`${id}l`} x1="0.9" y1="0" x2="0.1" y2="1">
            <stop offset="0" stopColor={GRAD[tone].ink} stopOpacity="0.55" />
            <stop offset="1" stopColor={GRAD[tone].ink} stopOpacity="0.12" />
          </linearGradient>
        )}
      </defs>
      {ART[kind]({ deep: `url(#${id}d)`, soft: `url(#${id}s)`, dot: b })}
    </svg>
  );
}

/** A label: a soft rectangle in a gradient, the words in its ink — a mark, never a button. */
export function Label({
  tone = 'stone',
  icon: Icon,
  white = false,
  children,
}: {
  tone?: Grad;
  icon?: FilledIcon;
  /** on a coloured card: white, the words in the card's ink */
  white?: boolean;
  children: ReactNode;
}) {
  return (
    <span
      className="inline-flex h-6 max-w-full shrink-0 items-center gap-1 rounded-[7px] px-2 text-[12px] leading-none font-medium whitespace-nowrap"
      style={
        white
          ? { backgroundColor: 'rgb(255 255 255 / 0.75)', color: GRAD[tone].ink }
          : { backgroundImage: grad(tone), color: GRAD[tone].ink }
      }
    >
      {Icon && <Icon size={13} weight="fill" aria-hidden className="shrink-0" />}
      <span className="truncate">{children}</span>
    </span>
  );
}

/** A Phosphor icon in a gradient circle: for what has no picture of its own. */
export function IconDot({
  icon: Icon,
  tone,
  size = 36,
}: {
  icon: FilledIcon;
  tone: Grad;
  size?: number;
}) {
  return (
    <span
      aria-hidden
      className="flex shrink-0 items-center justify-center rounded-full"
      style={{
        width: size,
        height: size,
        backgroundImage: grad(tone),
        color: GRAD[tone].ink,
      }}
    >
      <Icon size={Math.round(size * 0.44)} weight="fill" />
    </span>
  );
}

/** A field of a form: white with a hairline, radius 14 (as `Field` of components/ui). */
export const FIELD =
  'rounded-[14px] bg-white text-[15px] text-ink outline-none ring-1 ring-[#E4E0D7] ring-inset transition-shadow placeholder:text-faint focus:ring-2 focus:ring-[var(--color-primary)]';

/** A small heading inside a block: what the part under it is about. */
export function Sub({ children, count }: { children: ReactNode; count?: number }) {
  return (
    <h3 className="mb-2.5 flex items-baseline gap-2 text-[15px] font-medium" style={{ color: INK }}>
      {children}
      {count !== undefined && count > 0 && (
        <span className="text-[13px] font-normal" style={{ color: INK_FAINT }}>
          {count}
        </span>
      )}
    </h3>
  );
}
